'use client';

import { useMemo } from 'react';

import { cn } from '../../../libs/utils';
import { IconButton } from '../../button';
import { Card } from '../../organisms';

import { useCalendarDates } from './hooks/useCalendarDates';
import { SingleCalendarType } from './type/BaseCalendarType';

export const MonthCalendar = ({ baseDate, selectedDate, onSelectDate, disabled, className }: SingleCalendarType) => {
  const { renderedDate, handleNextYear, handlePrevYear, isDisabledRange, isDisabledBoundary } = useCalendarDates({ baseDate, disabled });

  const isPrevYearButtonDisabled = useMemo(() => {
    if (isDisabledBoundary(disabled)) {
      const isLastYear = disabled?.before?.getFullYear();

      return renderedDate.getFullYear() === isLastYear;
    }
    return false;
  }, [renderedDate, isDisabledBoundary, disabled]);

  const isNextYearButtonDisabled = useMemo(() => {
    if (isDisabledBoundary(disabled)) {
      const isLastestYear = disabled?.after?.getFullYear();

      return renderedDate.getFullYear() === isLastestYear;
    }
    return false;
  }, [renderedDate, isDisabledBoundary, disabled]);

  return (
    <div className={cn('w-full flex flex-col gap-y-tiny-large pb-tiny-medium', className)}>
      <div className={'p-tiny-large flex gap-tiny-large justify-between items-center'}>
        <IconButton size={'xsmall'} iconType={'chevron-left'} disabled={isPrevYearButtonDisabled} onClick={handlePrevYear} />
        <p className={'flex-1 body-large-semibold text-neutral-foreground-main text-center'}>{renderedDate?.getFullYear()}년</p>
        <IconButton size={'xsmall'} iconType={'chevron-right'} disabled={isNextYearButtonDisabled} onClick={handleNextYear} />
      </div>
      <div className="grid grid-cols-3 gap-tiny-large text-center body-small-semibold text-neutral-foreground-main">
        {new Array(12).fill('').map((_, index) => {
          const isSelected = selectedDate?.getMonth() === index && renderedDate.getFullYear() === selectedDate?.getFullYear();
          const targetDate = new Date(renderedDate.getFullYear(), index, 1);

          const getDisabledDay = () => {
            if (!disabled) {
              return false;
            } else if (isDisabledRange(disabled)) {
              return targetDate >= disabled.from && targetDate <= disabled.to;
            } else if (isDisabledBoundary(disabled)) {
              return targetDate < disabled.before || targetDate > disabled.after;
            }
            return false;
          };

          return (
            <Card
              outline={isSelected}
              onClick={() => {
                if (getDisabledDay()) return;
                onSelectDate(new Date(renderedDate.getFullYear(), index, 1));
              }}
              className={cn(
                '!p-normal-medium cursor-pointer hover:bg-neutral-background-hovered&pressed rounded-bzc-small',
                {
                  'ring-primary-border-main bg-primary-background-lowest ring-2 pointer-events-none': isSelected
                },
                { 'text-neutral-foreground-disabled pointer-events-none': getDisabledDay() }
              )}
              key={renderedDate.getFullYear() + index}
            >
              {index + 1}월
            </Card>
          );
        })}
      </div>
    </div>
  );
};
